/** @module AuthSegmentAnalytics */
/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */

import AuthAnalyticsBase from './auth-analytics-base.mjs';

class AuthSegmentAnalytics extends AuthAnalyticsBase {
  constructor(options = {}) {
    super(options);
    this._initPromise = null;
    this._initialized = false;
    // Store tenant class for auto-injection into all track payloads
    this._tenantClass = options.tenantClass ?? null;
  }

  /**
   * Initialize Segment Analytics by loading the script from CDN
   * Safe to call multiple times - subsequent calls return the same promise
   *
   * @param {Object} options - Configuration options
   * @param {string} options.writeKey - Segment write key (will be token-replaced)
   * @param {string} [options.tenantClass] - Tenant class (e.g., 'shipstation', 'shipstation-partners') for auto-injection into track payloads
   * @returns {Promise<void>}
   */
  async initialize(options = {}) {
    // If already initialized, return immediately
    if (this._initialized) {
      console.log('Segment already initialized');
      return Promise.resolve();
    }

    // If initialization in progress, return the existing promise
    if (this._initPromise) {
      console.log('Segment initialization in progress, waiting...');
      return this._initPromise;
    }

    // Start initialization
    this._initPromise = new Promise((resolve) => {
      try {
        const { writeKey } = options;
        if (!writeKey) {
          throw new Error('Segment writeKey is required for initialization');
        }

        console.log('Initializing Segment Analytics from CDN...', { writeKey });

        // Check if script already loaded by HTML snippet
        if (window.analytics && window.analytics.initialized) {
          console.log('Segment already loaded by HTML snippet');
          this._initialized = true;
          resolve();
          return;
        }

        // Create analytics stub if it doesn't exist
        if (!window.analytics) {
          window.analytics = [];
          window.analytics.invoked = true;
          window.analytics.methods = [
            'trackSubmit', 'trackClick', 'trackLink', 'trackForm', 'pageview',
            'identify', 'reset', 'group', 'track', 'ready', 'alias', 'debug',
            'page', 'once', 'off', 'on', 'addSourceMiddleware',
            'addIntegrationMiddleware', 'setAnonymousId', 'addDestinationMiddleware',
          ];

          // eslint-disable-next-line func-names
          window.analytics.factory = function (method) {
            // eslint-disable-next-line func-names
            return function () {
              // eslint-disable-next-line prefer-rest-params
              const args = Array.prototype.slice.call(arguments);
              args.unshift(method);
              window.analytics.push(args);
              return window.analytics;
            };
          };

          for (let i = 0; i < window.analytics.methods.length; i++) {
            const method = window.analytics.methods[i];
            window.analytics[method] = window.analytics.factory(method);
          }

          window.analytics._writeKey = writeKey;
          window.analytics.SNIPPET_VERSION = '4.15.3';
        }

        // Load analytics.js from CDN
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = `https://analyticscdn.shipstation.com/analytics.js/v1/${writeKey}/analytics.min.js`;

        script.onload = () => {
          console.log('Segment analytics.js loaded successfully');

          // Wait for analytics to be fully initialized
          const checkInitialized = setInterval(() => {
            if (window.analytics && window.analytics.initialized) {
              clearInterval(checkInitialized);
              this._initialized = true;
              console.log('Segment fully initialized');

              // Call initial page view
              window.analytics.page();

              resolve();
            }
          }, 50);

          // Timeout after 5 seconds
          setTimeout(() => {
            clearInterval(checkInitialized);
            if (!this._initialized) {
              console.warn('Segment initialization timeout');
              this._initialized = true; // Mark as initialized anyway to prevent retries
              resolve();
            }
          }, 5000);
        };

        script.onerror = (error) => {
          console.error('Failed to load Segment analytics.js', error);
          this._initialized = true; // Mark as initialized to prevent retries
          resolve(); // Resolve anyway - tracking failures should never block
        };

        // Insert script into page
        const firstScript = document.getElementsByTagName('script')[0];
        firstScript.parentNode.insertBefore(script, firstScript);
      } catch (error) {
        console.error('Segment initialization error:', error);
        this._initialized = true; // Mark as initialized to prevent retries
        resolve(); // Always resolve, never reject
      }
    });

    return this._initPromise;
  }

  /**
   * Track an event with Segment
   * @param {string} eventName - Name of the event to track
   * @param {object} properties - Event properties
   * @param {object} [context] - Additional context to enrich properties
   * @returns {Promise<void>} Promise that resolves when tracking completes (or times out after 300ms)
   *
   * @example
   * // Fire and forget (no await needed)
   * this._trackEvent(EVENT_USERNAME_ENTERED);
   *
   * @example
   * // Wait for tracking before navigation
   * await this._trackEvent(EVENT_CANCEL);
   * window.location.href = '/login';
   */
  async track(eventName, properties = {}, context = {}) {
    return new Promise((resolve) => {
      try {
        // Check if window.analytics is available
        if (!window.analytics || typeof window.analytics.track !== 'function') {
          console.warn(`Segment tracking skipped for "${eventName}" - window.analytics not available. Make sure Segment snippet is loaded in page template.`);
          resolve();
          return;
        }

        // Check if Segment has fully loaded (not just stub methods)
        const isSegmentReady = window.analytics.initialized || (window.analytics._loadOptions !== undefined);
        console.log(`Tracking event: ${eventName}`, {
          isSegmentReady,
          invoked: window.analytics.invoked,
          initialized: window.analytics.initialized,
          writeKey: window.analytics._writeKey,
        });
        console.log('Event properties:', properties, context);
        // Merge in any context provided by the caller
        const enrichedProperties = {
          ...properties,
          ...context,
          brand: 'ShipStation',
        };

        // Auto-inject tenant_class if set during initialization
        if (this._tenantClass) {
          enrichedProperties.tenant_class = this._tenantClass;
        }

        // Strip "auth0|" prefix from user_id if present
        if (enrichedProperties.user_id && typeof enrichedProperties.user_id === 'string') {
          enrichedProperties.user_id = enrichedProperties.user_id.replace(/^auth0\|/, '');
        }

        // Set a timeout as fallback in case Segment callback never fires
        const timeoutId = setTimeout(() => {
          console.warn('Segment tracking timeout (300ms), proceeding anyway');
          resolve();
        }, 300); // 300ms timeout

        // Use Segment's callback mechanism to know when tracking completes
        window.analytics.track(eventName, enrichedProperties, {}, () => {
          clearTimeout(timeoutId);
          resolve();
        });
      } catch (error) {
        console.error('Segment tracking error:', error);
        // Always resolve, never reject - tracking failures should never block UI
        resolve();
      }
    });
  }
}

export default AuthSegmentAnalytics;
